import { useState, useCallback, useEffect } from 'react';

export interface DailyGuide {
  id: number;
  userId: string;
  guideType: 'morning' | 'evening';
  dailyInsight: string;
  shortTermAction: string;
  shortTermTimeframe: string;
  shortTermCompleted?: boolean;
  mediumTermGoal: string;
  mediumTermTimeframe: string;
  mediumTermCompleted?: boolean;
  longTermVision: string;
  longTermTimeframe: string;
  longTermCompleted?: boolean;
  focusAreas: string[];
  connectedValues: string[];
  connectedGoals: string[];
  recentMomentsCount: number;
  generatedAt: Date;
  expiresAt: Date | null;
}

export function useDailyGuide() {
  const [dailyGuide, setDailyGuide] = useState<DailyGuide | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDailyGuide = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/daily-guide', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDailyGuide(data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching daily guide:', err);
      setError('Failed to fetch daily guide');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateDailyGuide = useCallback(async (forceRefresh = false) => {
    try {
      setIsGenerating(true);
      setError(null);
      const response = await fetch('/api/daily-guide/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ forceRefresh }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setDailyGuide(data);
        return data;
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to generate daily guide');
        return null;
      }
    } catch (err) {
      console.error('Error generating daily guide:', err);
      setError('Failed to generate daily guide');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const completeAction = useCallback(async (actionType: 'short' | 'medium' | 'long', completed: boolean) => {
    if (!dailyGuide) return;

    try {
      const response = await fetch(`/api/daily-guide/${dailyGuide.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ actionType, completed }),
      });

      if (response.ok) {
        const updated = await response.json();
        setDailyGuide(updated);
      }
    } catch (err) {
      console.error('Error toggling action completion:', err);
    }
  }, [dailyGuide]);

  const isExpired = useCallback(() => {
    if (!dailyGuide || !dailyGuide.expiresAt) return true;
    return new Date(dailyGuide.expiresAt) <= new Date();
  }, [dailyGuide]);

  useEffect(() => {
    fetchDailyGuide();
  }, [fetchDailyGuide]);

  return {
    dailyGuide,
    isLoading,
    isGenerating,
    error,
    isExpired,
    generateDailyGuide,
    completeAction,
    refetch: fetchDailyGuide,
  };
}
